import { useEffect } from "react";
import type { LocalMessage } from "stream-chat";
import {
  Message,
  MessageList,
  useChannelActionContext,
  useChannelStateContext,
} from "stream-chat-react";
import { MESSAGE_ACTIONS_WITH_THREADS } from "Components/Chat/chatMessageActions";
import { ChatThreadHeader } from "Components/Chat/ChatThreadHeader";

type InnerProps = {
  thread: LocalMessage;
};

const ChatPaymentThreadInner = ({ thread }: InnerProps) => {
  const {
    threadHasMore,
    threadLoadingMore,
    threadMessages = [],
  } = useChannelStateContext();
  const { closeThread, loadMoreThread } = useChannelActionContext();

  // First page of replies, as the SDK's Thread does; later pages come from the list's loadMore.
  useEffect(() => {
    if ((thread.reply_count ?? 0) > 0) {
      void loadMoreThread();
    }
  }, [thread, loadMoreThread]);

  // No ThreadStart ("N replies") under the card: the request card already shows the payments
  // count, inside the thread as well.
  const head = (
    <div className="str-chat__parent-message-li">
      <Message initialMessage threadList message={thread} />
    </div>
  );

  // Same class names as the SDK's panel so its CSS and ChatOverrides.css apply unchanged;
  // `chat-payment-thread` scopes the overrides that apply only to this payment-only thread.
  return (
    <div className="str-chat__thread-container str-chat__thread chat-payment-thread">
      <ChatThreadHeader closeThread={closeThread} thread={thread} />
      <MessageList
        head={head}
        threadList
        disableDateSeparator
        hasMore={threadHasMore}
        loadingMore={threadLoadingMore}
        loadMore={loadMoreThread}
        messages={threadMessages}
        messageActions={MESSAGE_ACTIONS_WITH_THREADS}
      />
    </div>
  );
};

/**
 * The thread panel: the parent request card on top and its payment replies below, and NO
 * composer. Replaces the SDK's <Thread> rather than blanking its Input, because <Thread> always
 * mounts a MessageInput whose composer hook keeps a module-level cache keyed by parent message id
 * and blind to the client. After a logout and a login as another user in the same tab, reopening
 * a thread served the first user's composer, whose channel belonged to the disconnected client
 * ("You can't use a channel after client.disconnect() was called"). No MessageInput, no cache.
 *
 * Threads exist in this chat only to collect the payments made against a multi-payer payment
 * request (opened from its card); replies are posted by the payment drawer, never typed, so a
 * composer would only let a payment thread turn into a conversation.
 */
export const ChatPaymentThread = () => {
  const { channel, channelConfig, thread } = useChannelStateContext();

  if (!thread || channelConfig?.replies === false) {
    return null;
  }

  // Keyed like the SDK's Thread so switching threads remounts the panel and its list state.
  return (
    <ChatPaymentThreadInner
      key={`thread-${thread.id}-${channel.cid}`}
      thread={thread}
    />
  );
};
